import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Cell
} from 'recharts';
import type { MatchScores } from '../../api/types';

interface Props {
  scores: MatchScores;
  compact?: boolean;
}

const COLORS = {
  event: '#7c3aed',      // purple
  location: '#2563eb',   // blue
  time: '#059669',       // green
  appearance: '#d97706', // amber
  mirrorConsistency: '#dc2626', // red
};

export default function ScoreBreakdown({ scores, compact = false }: Props) {
  const dimensions = [
    { key: 'event', label: 'Event', value: scores.event },
    { key: 'location', label: 'Location', value: scores.location },
    { key: 'time', label: 'Time', value: scores.time },
    { key: 'appearance', label: 'Appearance', value: scores.appearance },
    { key: 'mirrorConsistency', label: 'Mirror', value: scores.mirrorConsistency },
  ];

  if (compact) {
    return (
      <div className="flex flex-wrap gap-2">
        {dimensions.map((d) => (
          <div
            key={d.key}
            className="flex items-center gap-1 text-xs"
          >
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: COLORS[d.key as keyof typeof COLORS] }}
            />
            <span className="text-gray-500">{d.label}:</span>
            <span className="font-medium">{(d.value * 100).toFixed(0)}%</span>
          </div>
        ))}
        {scores.llmJudge != null && (
          <div className="flex items-center gap-1 text-xs">
            <span className="text-gray-500">LLM:</span>
            <span className="font-medium">{(scores.llmJudge * 100).toFixed(0)}%</span>
          </div>
        )}
      </div>
    );
  }

  const radarData = dimensions.map((d) => ({
    dimension: d.label,
    value: d.value * 100,
  }));

  const barData = dimensions.map((d) => ({
    name: d.label,
    value: d.value * 100,
    color: COLORS[d.key as keyof typeof COLORS],
  }));

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Radar Chart */}
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={radarData}>
            <PolarGrid />
            <PolarAngleAxis dataKey="dimension" tick={{ fontSize: 12 }} />
            <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10 }} />
            <Radar
              name="Score"
              dataKey="value"
              stroke="#7c3aed"
              fill="#7c3aed"
              fillOpacity={0.3}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {/* Bar Chart */}
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={barData} layout="vertical">
            <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10 }} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={80} />
            <Bar dataKey="value" radius={[0, 4, 4, 0]}>
              {barData.map((entry, index) => (
                <Cell key={index} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Summary Stats */}
      <div className="md:col-span-2 grid grid-cols-3 gap-4 text-center">
        <div className="p-3 bg-gray-50 rounded-lg">
          <div className="text-2xl font-bold text-gray-900">
            {(scores.overall * 100).toFixed(0)}%
          </div>
          <div className="text-xs text-gray-500">Deterministic</div>
        </div>
        <div className="p-3 bg-gray-50 rounded-lg">
          <div className="text-2xl font-bold text-pebbl-600">
            {scores.llmJudge != null ? `${(scores.llmJudge * 100).toFixed(0)}%` : '-'}
          </div>
          <div className="text-xs text-gray-500">LLM Judge</div>
        </div>
        <div className="p-3 bg-pebbl-50 rounded-lg">
          <div className="text-2xl font-bold text-pebbl-700">
            {scores.final != null ? `${(scores.final * 100).toFixed(0)}%` : `${(scores.overall * 100).toFixed(0)}%`}
          </div>
          <div className="text-xs text-pebbl-500">Final Score</div>
        </div>
      </div>
    </div>
  );
}
